import styled from 'styled-components';
import { Box, Flex, Grid } from '@wegroup/design-system';
import React, { useEffect } from 'react';
import LoadingScreen from '../../../../../../../common/src/components/LoadingScreen';
import useSessionId from '../../../../../../../flows/src/hooks/useSessionId';
import { useLouiseSelector } from '../../../../../store/redux/store';
import CompareOffersHoverButton from '../../../../comparison/components/CompareOffersHoverButton';
import { getUseProductsQKey, useProducts } from '../../../api/getProducts';
import OffersBottomBar from './OffersBottomBar';
import { mq } from '../../../../../../../common/src/utils/styledComponentsUtils';
import Avatar from '../../../../../components/default/ProdFacAvatar';
import ComparisonBar from '../../../../comparison/components/comparison-bar/ComparisonBar';
import useMobileBreakpoints from '../../../../../../../common/src/hooks/useMobileBreakpoints';
import OffersContainerHeader from './OffersOverviewHeader';
import NewOffersOverviewContent from '../overview-content/NewOffersOverviewContent';
import NewOffersOverviewSelectedRiskObjects from './NewOffersOverviewSelectedRiskObjects';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { setShownProductsOrder } from '../../../../../store/redux/slices/offersSlice';
import NewOffersBottomBar from './NewOffersBottomBar';
import { TOOLBAR_MAX_WIDTH } from '../../../../../components/toolbar/Toolbar';
import useSelectedQuotesStore from '../../../store/zustand/selectedQuotes';
import { useLocale } from '../../../../../../../../i18n';

interface Props {
  riskObject: string;
  setIsComparisonActive: (arg0: boolean) => void;
  addToSelectedOffers: (arg0: string) => void;
  removeFromSelectedOffers: (id?: string) => void;
}

const NewOffersOverviewContainer: React.FC<Props> = ({
  riskObject,
  setIsComparisonActive,
  addToSelectedOffers,
  removeFromSelectedOffers,
}) => {
  const { userLang } = useLocale();
  const sessionId = useSessionId();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resetSelectedQuotes = useSelectedQuotesStore((state) => state.clear);
  const { data: products, isLoading } = useProducts({
    sessionId,
    insuranceType: riskObject,
    lang: userLang.toUpperCase(),
  });

  const { isLaptop } = useMobileBreakpoints();

  const comparisonMatrixOffers = useLouiseSelector(
    (state) => state.offers.comparisonMatrixOffers,
  );

  useEffect(() => {
    /** When going back in the flow and changing questions, the products should refetch */
    return () => {
      queryClient.removeQueries(
        getUseProductsQKey({
          sessionId,
          insuranceType: riskObject,
        }),
      );
    };
  }, []);

  useEffect(() => {
    resetSelectedQuotes();
    return () => {
      resetSelectedQuotes();
    };
  }, [products]);

  useEffect(() => {
    /** Because backend will sort the products response by quality, we make sure the order is set to that if possible */
    if (products?.items.length) {
      if (products.items.some(({ matching_score }) => !!matching_score)) {
        dispatch(setShownProductsOrder('QUALITY_DESC'));
      }
    }
  }, [products?.items.length]);

  if (isLoading) return <LoadingScreen />;

  return (
    <>
      <Flex
        flexDirection="column"
        h={{ laptop: '100%' }}
        minH="100%"
        overflowY={{ laptop: 'auto' }}
        pb={{
          base: products?.items ? '32' : 0,
          laptop: comparisonMatrixOffers[riskObject]?.length > 0 ? '56' : '32',
        }}
      >
        <Grid
          w="100%"
          margin="0 auto"
          flexGrow="1"
          gridGap="6"
          gridTemplateColumns={{
            base: '1fr',
            laptopL: '7.6rem minmax(auto, 100rem)',
          }}
          maxW="7xl"
          paddingX={{ base: '6', laptop: '14' }}
          paddingY={{ base: '4', laptop: '6' }}
        >
          {comparisonMatrixOffers[riskObject]?.length > 0 && (
            <Box display={{ laptop: 'none' }}>
              <CompareOffersHoverButton
                onClick={() => setIsComparisonActive(true)}
              />
            </Box>
          )}
          <StyledAvatar />
          <Flex direction="column">
            <OffersContainerHeader
              riskObject={riskObject}
              riskObjectErrors={products?.risk_object_errors || []}
            />
            <NewOffersOverviewSelectedRiskObjects
              sessionId={sessionId}
              type={riskObject}
            />
            <NewOffersOverviewContent
              products={products?.items || []}
              productContentItems={products?.info_and_content || []}
              isLoading={isLoading}
            />
          </Flex>
        </Grid>
        {comparisonMatrixOffers[riskObject]?.length > 0 && isLaptop && (
          <Box
            w="calc(100% - 12px)"
            boxShadow="level1"
            position="fixed"
            transform="auto"
            transition="0.2s"
            bottom="0"
            left="0"
            pl={{
              ...TOOLBAR_MAX_WIDTH,
              base: 0,
              laptop: TOOLBAR_MAX_WIDTH.base,
            }}
            bg="white"
          >
            <ComparisonBar
              riskObject={riskObject}
              setIsComparisonActive={setIsComparisonActive}
            />
          </Box>
        )}
      </Flex>
      <NewOffersBottomBar />
      <OffersBottomBar riskObject={riskObject} />
    </>
  );
};

const StyledAvatar = styled(Avatar)`
  display: none;

  ${mq.laptopL} {
    display: block;
    width: 7.6rem;
    height: 7.6rem;
  }
`;

export default NewOffersOverviewContainer;
